import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  appointmentActions,
  appointmentDraftSelectors,
  appointmentToAppointmentDraftComparisonSelectors,
  extractClientDetails,
  journeyStateActions,
  updateClientDetails,
  appointmentDraftActions,
} from '~/api';
import {
  apiCallFinished,
  envConfigSelectors,
  errorMessagesActions,
  questionHelpers,
  ScreenNav,
  withPreloadAppointment,
  withProgressBar,
  withRequiredPrecondition,
  withRestorableDraft,
} from '~/components';
import { restoreAppointmentData } from '~/routes/helpers';
import { capitalize, getSafety, isBoolean } from '../../utils';
import { AboutIntro } from './about-intro';
import { AboutYouForm } from './about-you-form';
import { TimeSlot } from './time-slot';

const firstNameField = 'yourDetailsSection.firstName';
const lastNameField = 'yourDetailsSection.lastName';

export class AboutYou extends PureComponent {
  state = {
    isAjaxCompleted: false,
  };

  componentDidMount() {
    const { dispatchClearOnlyErrorMessages } = this.props;

    dispatchClearOnlyErrorMessages();
    this.preloadData();
  }

  handleCreateAppointmentError = (response) => {
    const { status } = response;
    if (status !== 409) {
      Promise.reject(response);
    }
  };

  createAppointment = () => {
    const {
      dispatchCreateAppointment,
      dispatchApiCallFinished,
      dispatchRestoreAppointment,
      appointmentDraft,
    } = this.props;
    const {
      appointmentType: { id: appointmentTypeId },
    } = appointmentDraft;

    dispatchApiCallFinished(false);

    dispatchCreateAppointment(appointmentTypeId).then(() => {
      dispatchRestoreAppointment(false, {}).then(() => {
        dispatchApiCallFinished(true);
        this.setState({ isAjaxCompleted: true });
      });
    }, this.handleCreateAppointmentError);
  };

  preloadData = () => {
    const {
      isAppointmentTypeIdChanged,
      isAppointmentChanged,
      appointmentDraft,

      dispatchApiCallFinished,
      dispatchUpdateAppointment,
      dispatchRestoreAppointment,
    } = this.props;
    const { isAjaxCompleted } = this.state;
    if (!isAjaxCompleted) {
      const groupSelected = !!appointmentDraft.partnersGroupId;
      dispatchApiCallFinished(false);

      if (groupSelected || isAppointmentTypeIdChanged) {
        this.createAppointment();
      } else if (isAppointmentChanged) {
        dispatchUpdateAppointment().then(() =>
          dispatchRestoreAppointment(false, {}).then(() => {
            dispatchApiCallFinished(true);
            this.setState({ isAjaxCompleted: true });
          }),
        );
      } else {
        dispatchApiCallFinished(true);
        this.setState({ isAjaxCompleted: true });
      }
      window.scrollTo(0, 0);
    }
  };

  onSubmit = (formValues, form) => {
    const {
      questions,
      address,
      history,
      appointmentDraft,
      dispatchClearErrorMessages,
      dispatchApiCallFinished,
      dispatchSetPatchTestPassed,
      dispatchSaveJourneyState,
      displayPatchTestSection,
      appointmentDraft: { fittingTypes },
      dispatchUpdateAppointment,
    } = this.props;

    dispatchClearErrorMessages();
    dispatchApiCallFinished(false);

    const clientDetails = extractClientDetails(formValues.yourDetailsSection);
    form.change(firstNameField, capitalize(clientDetails.firstName));
    form.change(lastNameField, capitalize(clientDetails.lastName));

    const isFittingTypes = Object.values(fittingTypes).filter((e) => e).length > 0;

    let modifiedFormValues = { ...formValues };
    if (isFittingTypes) {
      const fittingTypeQuestions = questions.RETRIEVED_QUESTIONS.filter(({ title }) =>
        title.includes('Child'),
      );
      fittingTypeQuestions.map((question, index) => {
        const keyName = `q${question.id}`;
        const value = [fittingTypes[index + 1]];
        modifiedFormValues = {
          ...modifiedFormValues,
          questionsSection: {
            ...modifiedFormValues.questionsSection,
            [keyName]: value,
          },
        };
      });
    }
    return dispatchUpdateAppointment({
      answers: questionHelpers.getFormAnswers(
        questions,
        modifiedFormValues,
        address,
        appointmentDraft.appointmentType.id,
      ),
    })
      .then(() => updateClientDetails(clientDetails))
      .then(() => {
        if (displayPatchTestSection) {
          dispatchSetPatchTestPassed(true);
          return dispatchSaveJourneyState();
        }
        return Promise.resolve();
      })
      .then(() => history.pushWithToken('/review'))
      .catch(this.handleCreateAppointmentError);
  };

  constructInitialValues = () => {
    const {
      clientDetails,
      questions,
      config: { questionsSectionsConfig },
      restoredAnswers: {
        yourDetailsSection,
        questionsSection,
        termsConditions = {},
        analyticsQuestionSection,
      },
      patchTestPassed,
      selectedServiceTypeChanged,
    } = this.props;

    const displayQuestionsInYourDetailsSection =
      questionsSectionsConfig[0]?.displayQuestionsInYourDetails;
    const questionsDefaultValues = Object.values(questions).reduce((acc, curr) => {
      curr
        .filter((question) => question.defaultValue || isBoolean(question.defaultValue))
        .forEach((question) => {
          acc[question.reduxFieldConfig.name] = question.defaultValue;
        });
      return acc;
    }, {});

    const initialValues = {
      yourDetailsSection: {
        ...clientDetails,
        ...yourDetailsSection, // in case of page reloading on review page
      },
      questionsSection: {
        ...(selectedServiceTypeChanged
          ? {}
          : {
              ...(displayQuestionsInYourDetailsSection ? {} : questionsDefaultValues),
              ...(displayQuestionsInYourDetailsSection ? {} : questionsSection),
            }),
      },
      termsConditions: {
        termsConditions: termsConditions.termsConditions || '',
      },
      analyticsQuestionSection: {
        ...analyticsQuestionSection,
      },
      patchTestSection: {
        patchTest: patchTestPassed ? 'true' : '',
      },
    };

    return initialValues;
  };

  onPrevClick = () => {
    const { dispatchClearAppointment, prevPath, history } = this.props;
    dispatchClearAppointment();
    history.pushWithToken(prevPath);
  };

  getTimeSlot = () => {
    const {
      appointmentDraft: {
        dateSlot: selectedDateSlot,
        timeSlot: selectedTimeSlot,
        formatTimeSlot,
        prevPath,
        timeSlotTitle,
      },
      config: { hideAboutYouTimeSlotComponent },
    } = this.props;
    const canRenderTimeSlotComponent = !hideAboutYouTimeSlotComponent && selectedDateSlot;

    return canRenderTimeSlotComponent ? (
      <TimeSlot
        selectedDate={selectedDateSlot}
        startTime={selectedTimeSlot.from}
        endTime={selectedTimeSlot.to}
        prevPath={prevPath}
        formatTimeSlot={formatTimeSlot}
        title={timeSlotTitle || 'Your appointment time'}
      />
    ) : null;
  };

  getFilteredQuestions = () => {
    const {
      appointmentDraft: { fittingTypes },
      questions,
    } = this.props;
    const { isAjaxCompleted } = this.state;

    const isFittingTypes = Object.values(fittingTypes || {}).filter((e) => e).length > 0;
    let filteredQuestions = { ...questions };
    if (isFittingTypes && isAjaxCompleted) {
      filteredQuestions.RETRIEVED_QUESTIONS = [
        ...questions.RETRIEVED_QUESTIONS.filter(({ title }) => !title.includes('Child')),
      ];
    }

    return filteredQuestions;
  };

  render() {
    const {
      prevPath,
      appointmentDraft: {
        shop: { id: shopId },
      },
      location: { pathname },
      config: { AboutYouForm: aboutYouFormConfig = {}, questionsSectionsConfig },
      displayPatchTestSection,
      aboutIntroConfig,
      selectedServiceTypeChanged,
      selectedAppointmentID,
      dispatchSelectedServiceTypeChanged,
    } = this.props;
    const { isAjaxCompleted } = this.state;

    return (
      <section data-testid="AboutYou" data-test-ajax={isAjaxCompleted}>
        {aboutIntroConfig && <AboutIntro {...aboutIntroConfig} />}
        <AboutYouForm
          formId="AboutYouForm"
          initialValues={this.constructInitialValues()}
          onSubmit={(values, form) => this.onSubmit(values, form)}
          questions={this.getFilteredQuestions()}
          pathname={pathname}
          shopId={shopId}
          questionsSectionsConfig={questionsSectionsConfig}
          timeSlotComponent={this.getTimeSlot()}
          displayPatchTestSection={displayPatchTestSection}
          shouldDestroyFormOnUnmount={selectedServiceTypeChanged}
          selectedAppointmentID={selectedAppointmentID}
          dispatchFormDestroyOnUnmount={dispatchSelectedServiceTypeChanged}
          {...aboutYouFormConfig}
        ></AboutYouForm>
        <ScreenNav
          prevPath={prevPath}
          onPrevClick={this.onPrevClick}
          submit
          formId="AboutYouForm"
        />
      </section>
    );
  }
}

export const mapStateToProps = (state) => {
  const {
    questions: { questions, restoredAnswers },
    client: { details: clientDetails },
    services: { config },
    appointment: { draft: appointmentDraft },
    journeyState: { patchTestPassed },
  } = state;

  const {
    appointmentType: { id: selectedAppointmentID },
    selectedServiceTypeChanged,
  } = appointmentDraft;

  return {
    isAppointmentTypeIdChanged:
      !appointmentToAppointmentDraftComparisonSelectors.areAppointmentTypeIdsTheSame(state),
    isAppointmentChanged:
      !appointmentToAppointmentDraftComparisonSelectors.areAppointmentsTheSame(state),
    clientDetails,
    questions,
    restoredAnswers,
    config,
    displayPatchTestSection: !!getSafety(
      appointmentDraft.appointmentType,
      'beauty.patchOrAllergyTest',
      undefined,
    ),
    appointmentDraft,
    address: appointmentDraftSelectors.selectAddress(state),
    prevPath: config.aboutYouPrevPath,
    patchTestPassed,
    timeSlotTitle: envConfigSelectors.selectAboutYouTimeSlotTitle(
      appointmentDraft.appointmentType.id,
    )(state),
    formatTimeSlot: envConfigSelectors.selectFormatTimeSlot(appointmentDraft.appointmentType.id)(
      state,
    ),
    aboutIntroConfig: envConfigSelectors.selectAboutIntro(appointmentDraft.appointmentType.id)(
      state,
    ),
    selectedServiceTypeChanged,
    selectedAppointmentID,
  };
};

const mapDispatchToProps = {
  dispatchRestoreAppointment: restoreAppointmentData,
  dispatchClearErrorMessages: errorMessagesActions.clearErrorMessages,
  dispatchClearOnlyErrorMessages: errorMessagesActions.clearOnlyErrorMessages,
  dispatchApiCallFinished: apiCallFinished,
  dispatchClearAppointment: appointmentActions.clearAppointment,
  dispatchUpdateAppointment: appointmentActions.updateAppointment,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchSetPatchTestPassed: journeyStateActions.setPatchTestPassed,
  dispatchSaveJourneyState: journeyStateActions.saveJourneyState,
  dispatchGetAppointment: appointmentActions.getAppointment,
  dispatchCreateAppointment: appointmentActions.createAppointment,
  dispatchSelectedServiceTypeChanged: appointmentDraftActions.setSelectedServiceTypeChanged,
};

export const ConnectedAboutYou = withRestorableDraft(
  withRequiredPrecondition(
    connect(mapStateToProps, mapDispatchToProps)(withProgressBar(withPreloadAppointment(AboutYou))),
  ),
);

AboutYou.propTypes = {
  history: PropTypes.shape({}).isRequired,
  prevPath: PropTypes.string.isRequired,
  clientDetails: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
  }).isRequired,
  config: PropTypes.shape({}).isRequired,
  questions: PropTypes.shape({}).isRequired,
  restoredAnswers: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  appointmentDraft: PropTypes.shape({
    appointmentType: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    shop: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
    dateSlot: PropTypes.string.isRequired,
    timeSlot: PropTypes.shape({
      from: PropTypes.number,
      to: PropTypes.number,
    }).isRequired,
  }).isRequired,
  address: PropTypes.shape({}).isRequired,
  patchTestPassed: PropTypes.bool.isRequired,
  displayPatchTestSection: PropTypes.bool.isRequired,
  isAppointmentTypeIdChanged: PropTypes.bool.isRequired,
  isAppointmentChanged: PropTypes.bool.isRequired,
  timeSlotTitle: PropTypes.string,
  formatTimeSlot: PropTypes.func,
  aboutIntroConfig: PropTypes.shape({}),

  dispatchClearErrorMessages: PropTypes.func.isRequired,
  dispatchClearOnlyErrorMessages: PropTypes.func.isRequired,
  dispatchApiCallFinished: PropTypes.func.isRequired,
  dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
  dispatchClearAppointment: PropTypes.func.isRequired,
  dispatchRestoreAppointment: PropTypes.func.isRequired,
  dispatchSetPatchTestPassed: PropTypes.func.isRequired,
  dispatchSaveJourneyState: PropTypes.func.isRequired,
  dispatchGetAppointment: PropTypes.func.isRequired,
  dispatchCreateAppointment: PropTypes.func.isRequired,

  dispatchUpdateAppointment: PropTypes.func.isRequired,
  selectedServiceTypeChanged: PropTypes.bool.isRequired,
};

AboutYou.defaultProps = {
  timeSlotTitle: undefined,
  formatTimeSlot: undefined,
  aboutIntroConfig: undefined,
};
